import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';
import { fill } from '../i18n';
import type { AIGeneratedPlan } from '../data/projectTemplates';
import { findBestTemplate } from '../data/projectTemplates';
import { calculateEndDate, getNextWorkDay } from '../utils/dateUtils';
import { estimateHoursFromDuration } from '../utils/duration';
import { projectService } from './projectService';
import { taskService } from './taskService';
import { teamService } from './teamService';
import { capacityService } from './capacityService';
import { taskAssignmentService } from './taskAssignmentService';
import type { TaskCreateInput, ProjectWithStats } from '../types';

export interface GenerateProjectResponse {
  plan: AIGeneratedPlan;
  source: 'ai' | 'template';
  warning?: string;
}

export interface AIProjectCreationResult {
  projectId: string;
  taskCount: number;
  teamCount: number;
  allocationCount: number;
  /** Projeto criado, já com stats derivados, para registrar no contexto sem re-consultar o servidor. */
  project: ProjectWithStats;
}

/**
 * Executa `worker` sobre `items` com concorrência limitada (evita saturar a conexão
 * ao paralelizar dezenas de updates do seed). Mantém o ganho do paralelismo sem
 * disparar todos os round-trips de uma vez.
 */
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<unknown>
): Promise<void> {
  for (let i = 0; i < items.length; i += limit) {
    await Promise.all(items.slice(i, i + limit).map(worker));
  }
}

/**
 * Chama o Edge Function para gerar um plano de projeto via IA
 */
export async function generateProjectPlan(
  description: string,
  complexity: 'simple' | 'medium' | 'complex' = 'medium',
  targetWeeks?: number
): Promise<GenerateProjectResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error(getCurrentCopy().common.serviceErrors.userNotAuthenticated);

  try {
    const { data, error } = await supabase.functions.invoke('ai-project-generate', {
      body: {
        description,
        complexity,
        targetWeeks,
        accessToken: session.access_token
      }
    });

    if (error) {
      let errorMsg = getCurrentCopy().common.serviceErrors.aiUnavailable;

      // error.context É o Response object diretamente (FunctionsHttpError)
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const body = await ctx.json();
          if (body?.error) errorMsg = body.error;
          if (body?.limit) {
            throw new Error(fill(getCurrentCopy().common.serviceErrors.aiDailyLimit, { limit: body.limit, plan: body.plan }));
          }
        } catch (parseErr) {
          if ((parseErr as Error).message?.includes('Limite diário')) throw parseErr;
        }
      }

      console.warn('[AI] Edge Function erro:', errorMsg);
      return {
        plan: findBestTemplate(description),
        source: 'template',
        warning: `${errorMsg}. Usando template da biblioteca — tente novamente.`
      };
    }

    return {
      plan: data.plan,
      source: data.source,
      warning: data.warning
    } as GenerateProjectResponse;
  } catch (err) {
    // Se for erro de rate limit, propagar
    if ((err as Error).message?.includes('Limite diário')) {
      throw err;
    }
    // Qualquer outro erro: fallback para template
    const errorMsg = (err as Error).message || String(err);
    console.warn('[AI] Erro ao gerar plano, usando template:', errorMsg);
    return {
      plan: findBestTemplate(description),
      source: 'template',
      warning: getCurrentCopy().common.serviceErrors.aiFallback
    };
  }
}

/**
 * Cria o projeto completo no Supabase a partir de um plano gerado pela IA.
 * Orquestra: projeto → equipe → fases (groups) → tarefas → dependências
 */
export async function createProjectFromAIPlan(
  plan: AIGeneratedPlan,
  userId: string,
  startDate: Date,
  workDays: number[] = [1, 2, 3, 4, 5],
  holidays: string[] = []
): Promise<AIProjectCreationResult> {
  // 1. Criar o projeto (usa RPC atômica, bypassa RLS)
  const newProject = await projectService.create({
    name: plan.project.name,
    description: plan.project.description,
    // `excludedDates` e NÃO `holidays`: esta função gravava a chave que o
    // cronograma nunca leu, então projeto criado pela IA nascia com feriado
    // decorativo, que não pulava um dia sequer no cálculo de datas.
    settings: { workDays, hoursPerDay: 8, currency: 'BRL', excludedDates: holidays }
  }, userId);

  if (!newProject) {
    throw new Error('Falha ao criar projeto');
  }

  const projectId = newProject.id;

  try {
    // 2. Criar equipe
    let createdTeam: Array<{ id: string; name: string; role_title: string }> = [];
    if (plan.team && plan.team.length > 0) {
      const teamMembers = await teamService.createMany(
        plan.team.map(member => ({
          project_id: projectId,
          name: member.name,
          role_title: member.role_title,
          avatar_color: member.avatar_color
        }))
      );
      createdTeam = teamMembers.map(m => ({ id: m.id, name: m.name, role_title: m.role_title || '' }));

      // Sync team members to capacity resources (if available) — em paralelo (independentes).
      await runWithConcurrency(
        teamMembers.filter(m => m.id),
        15,
        member => capacityService.ensureResource(member.id).catch(() => {/* ignore if capacity not available */})
      );
    }

    // 3. Criar fases (groups) primeiro para obter IDs
    const phaseInputs: TaskCreateInput[] = plan.phases.map((phase, idx) => ({
      project_id: projectId,
      name: phase.name,
      type: 'group' as const,
      duration: '0d',
      color: phase.color,
      position: idx,
      expanded: true,
      description: ''
    }));

    const createdPhases = await taskService.createMany(phaseInputs);

    // 4. Construir lista flat de tarefas com cálculo de datas
    const allTaskInputs: TaskCreateInput[] = [];
    const globalIndexToLocalIndex: number[] = [];
    let currentDate = getNextWorkDay(startDate, workDays, holidays);

    // Mapa de papel → member ID. A chave é NORMALIZADA (sem acento, sem caixa,
    // sem espaço extra) porque os dois lados vêm da IA em campos diferentes
    // (team[].role_title e task.assigned_role) e divergem por acento ou caixa
    // com frequência. Casar por string crua fazia a atribuição virar null em
    // silêncio, e com o projeto em outro idioma isso aconteceria sempre.
    const roleKey = (v: string): string =>
      v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

    const roleToId: Record<string, string> = {};
    for (const member of createdTeam) {
      if (member.role_title) roleToId[roleKey(member.role_title)] = member.id;
    }

    for (let phaseIdx = 0; phaseIdx < plan.phases.length; phaseIdx++) {
      const phase = plan.phases[phaseIdx];
      const phaseId = createdPhases[phaseIdx]?.id;
      if (!phaseId) continue;

      for (let taskIdx = 0; taskIdx < phase.tasks.length; taskIdx++) {
        const task = phase.tasks[taskIdx];
        const isMilestone = task.type === 'milestone';

        // Calcular start_date baseado na dependência
        let taskStartDate: Date;
        if (task.depends_on_index !== undefined && task.depends_on_index >= 0) {
          const depLocalIdx = globalIndexToLocalIndex[task.depends_on_index];
          if (depLocalIdx !== undefined && allTaskInputs[depLocalIdx]) {
            const depTask = allTaskInputs[depLocalIdx];
            const depEndDate = depTask.end_date ? new Date(depTask.end_date as string) : currentDate;
            taskStartDate = getNextWorkDay(
              new Date(depEndDate.getTime() + 86400000),
              workDays,
              holidays
            );
          } else {
            taskStartDate = new Date(currentDate);
          }
        } else if (phaseIdx === 0 && taskIdx === 0) {
          taskStartDate = new Date(currentDate);
        } else {
          taskStartDate = new Date(currentDate);
        }

        const taskEndDate = isMilestone
          ? taskStartDate
          : calculateEndDate(taskStartDate, task.duration, workDays, holidays);

        // Atualizar currentDate para a próxima tarefa sem dependência explícita
        if (task.depends_on_index !== undefined || isMilestone) {
          currentDate = new Date(taskEndDate);
        }

        let assignedId: string | null = null;
        if (task.assigned_role) {
          assignedId = roleToId[roleKey(task.assigned_role)] || null;
          if (!assignedId) {
            // Não silenciar: papel que não casa com ninguém da equipe é sinal de
            // resposta inconsistente da IA, não de tarefa sem responsável.
            console.warn('[aiProject] papel sem correspondente na equipe:', task.assigned_role);
          }
        }

        const localIdx = allTaskInputs.length;
        globalIndexToLocalIndex.push(localIdx);

        const taskDuration = task.duration || '1d';
        const rawEstimatedHours = (task as { estimated_hours?: number | null }).estimated_hours;
        const estimatedHours = typeof rawEstimatedHours === 'number' && rawEstimatedHours > 0
          ? rawEstimatedHours
          : (isMilestone ? 0 : estimateHoursFromDuration(taskDuration, 8));

        allTaskInputs.push({
          project_id: projectId,
          parent_id: phaseId,
          name: task.name,
          type: isMilestone ? 'milestone' : 'task',
          duration: taskDuration,
          start_date: formatDateLocal(taskStartDate),
          end_date: formatDateLocal(taskEndDate),
          progress: 0,
          assigned: assignedId,
          estimated_hours: estimatedHours > 0 ? estimatedHours : null,
          description: task.description || '',
          position: taskIdx,
          dependencies: [],
          expanded: true
        });
      }
    }

    // 5. Criar todas as tarefas
    const createdTasks = await taskService.createMany(allTaskInputs);

    // 5.5 Sync task_assignments junction table
    const assignmentEntries = createdTasks
      .filter(t => t.assigned)
      .map(t => ({
        task_id: t.id,
        team_member_id: t.assigned!,
        is_primary: true,
      }));
    if (assignmentEntries.length > 0) {
      taskAssignmentService.bulkAssign(assignmentEntries).catch(() => {});
    }

    // 6. Mapear dependências com UUIDs reais — coletar e atualizar em paralelo (cada
    //    tarefa é um update independente).
    const dependencyUpdates: Array<{ id: string; dependencies: string[] }> = [];
    let globalIdx = 0;
    for (const phase of plan.phases) {
      for (const task of phase.tasks) {
        if (task.depends_on_index !== undefined && task.depends_on_index >= 0) {
          const depGlobalIdx = task.depends_on_index;
          const depLocalIdx = globalIndexToLocalIndex[depGlobalIdx];
          const currentLocalIdx = globalIndexToLocalIndex[globalIdx];

          if (
            depLocalIdx !== undefined &&
            currentLocalIdx !== undefined &&
            createdTasks[depLocalIdx] &&
            createdTasks[currentLocalIdx]
          ) {
            dependencyUpdates.push({
              id: createdTasks[currentLocalIdx].id,
              dependencies: [createdTasks[depLocalIdx].id]
            });
          }
        }
        globalIdx++;
      }
    }
    await runWithConcurrency(dependencyUpdates, 15, u =>
      taskService.update(u.id, { dependencies: u.dependencies })
    );

    // 7. Atualizar datas das fases (group) com base nos filhos — em paralelo.
    const phaseDateUpdates: Array<{ id: string; start_date: string; end_date: string }> = [];
    for (let phaseIdx = 0; phaseIdx < createdPhases.length; phaseIdx++) {
      const phaseTaskInputs = allTaskInputs.filter(t => t.parent_id === createdPhases[phaseIdx].id);
      if (phaseTaskInputs.length > 0) {
        const phaseStart = phaseTaskInputs.reduce((min, t) => {
          const d = t.start_date ? new Date(t.start_date as string) : new Date();
          return d < min ? d : min;
        }, new Date('2099-12-31'));

        const phaseEnd = phaseTaskInputs.reduce((max, t) => {
          const d = t.end_date ? new Date(t.end_date as string) : new Date();
          return d > max ? d : max;
        }, new Date('1970-01-01'));

        phaseDateUpdates.push({
          id: createdPhases[phaseIdx].id,
          start_date: formatDateLocal(phaseStart),
          end_date: formatDateLocal(phaseEnd)
        });
      }
    }
    await runWithConcurrency(phaseDateUpdates, 15, u =>
      taskService.update(u.id, { start_date: u.start_date, end_date: u.end_date })
    );

    // 8. Gerar alocações iniciais para o time (PLANO de capacidade)
    //    Usa estimated_hours das tasks para derivar hours_per_week por membro.
    let allocationCount = 0;
    if (createdTeam.length > 0 && allTaskInputs.length > 0) {
      const projectStart = allTaskInputs.reduce((min, t) => {
        const d = t.start_date ? new Date(t.start_date as string) : new Date();
        return d < min ? d : min;
      }, new Date('2099-12-31'));
      const projectEnd = allTaskInputs.reduce((max, t) => {
        const d = t.end_date ? new Date(t.end_date as string) : new Date();
        return d > max ? d : max;
      }, new Date('1970-01-01'));

      const allocations = await capacityService.generateAllocationsFromProject({
        projectId,
        teamMembers: createdTeam.map(m => ({ id: m.id, role_title: m.role_title })),
        projectStart: formatDateLocal(projectStart),
        projectEnd: formatDateLocal(projectEnd),
        tasks: allTaskInputs.map(t => ({
          assigned: (t.assigned as string | null) ?? null,
          estimated_hours: (t.estimated_hours as number | null) ?? null,
          duration: (t.duration as string | null) ?? null,
        })),
        projectSettings: newProject.settings ?? { workDays, hoursPerDay: 8, holidays },
      });
      allocationCount = allocations.length;
    }

    // Stats derivados localmente (sem re-consultar o servidor): conta só tarefas
    // reais (exclui groups/milestones), espelhando getUserProjectsWithStats.
    const realTaskCount = allTaskInputs.filter(t => t.type !== 'milestone' && t.type !== 'group').length;
    const projectWithStats: ProjectWithStats = {
      ...newProject,
      stats: { total: realTaskCount, completed: 0, overdue: 0 }
    } as ProjectWithStats;

    return {
      projectId,
      taskCount: createdTasks.length,
      teamCount: createdTeam.length,
      allocationCount,
      project: projectWithStats
    };

  } catch (err) {
    // Rollback: deletar o projeto se a criação de tarefas falhou
    console.error('Erro ao criar tarefas, deletando projeto:', err);
    await supabase.from('projects').delete().eq('id', projectId);
    throw err;
  }
}

/**
 * Formata uma data para string YYYY-MM-DD preservando o fuso horário local
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
